import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const DataResidency: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Data Residency — Sprout Docs</title>
        <meta
          name="description"
          content="Where Sprout stores customer data: PostgreSQL in EU-Central-1 (Frankfurt), application hosting on Render, S3-compatible object storage, and how this aligns with POPIA cross-border transfer rules."
        />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/docs/security/data-residency" />
      </Helmet>

      <h1>Data Residency</h1>
      <p className="lead">
        Where your data physically lives matters, especially under POPIA. This
        page is direct about where Sprout stores customer information today
        and the tradeoffs behind those choices.
      </p>

      <h2>Production database</h2>
      <p>
        Customer data is stored in a managed PostgreSQL cluster on Supabase,
        in the EU-Central-1 region (Frankfurt, Germany). This is where every
        observation, assessment, child record, and audit log lives.
      </p>

      <h2>Why EU and not South Africa?</h2>
      <p>
        We considered SA-hosted options but the managed PostgreSQL market in
        South Africa is currently limited, and the alternatives tend to be
        less mature on operational security and backup tooling. The European
        Union sits under the GDPR, which provides a comparable level of
        protection to POPIA. Cross-border transfers under POPIA Section 72
        require the recipient to be subject to a law, binding corporate
        rules, or binding agreement that provides an adequate level of
        protection — we rely on the third option (Standard Contractual
        Clauses with our infrastructure providers), which is the standard
        mechanism SA businesses use for EU-hosted infrastructure.
      </p>
      <p>
        We treat this as a tradeoff, not a feature. If you need SA-only
        hosting for procurement reasons, get in touch and we can discuss a
        custom deployment.
      </p>

      <h2>Application hosting</h2>
      <p>
        The Sprout API runs on Render, and the marketing and main UI run on
        Vercel. Both providers support multi-region deployment and provide
        managed TLS termination. Application logs do not contain personal
        information by design.
      </p>

      <h2>Media files</h2>
      <p>
        Photos and short videos that teachers attach to observations are
        stored in S3-compatible object storage. In production this lives in
        the same EU region as the database; in development it's a local
        MinIO instance. All media URLs are short-lived signed URLs — they're
        not publicly addressable.
      </p>

      <h2>Cross-border transfer protections</h2>
      <ul>
        <li>All transfers between client, application, and database are encrypted with TLS 1.2 or higher.</li>
        <li>
          Cross-border processing is covered by Standard Contractual Clauses
          with each of our infrastructure providers and other subprocessors.
          See <Link to="/legal/subprocessors">Subprocessors</Link> for the
          full list, region of each, and the safeguard mechanism that
          applies.
        </li>
        <li>Backups remain within the EU region and are encrypted at rest.</li>
      </ul>

      <h2>For schools that require SA-only hosting</h2>
      <p>
        Some districts and large school groups have procurement rules
        requiring that data stay inside South Africa. This isn't part of our
        standard plan today, but if it's a hard requirement for you,{' '}
        <Link to="/contact">get in touch</Link> and we can scope a custom
        deployment.
      </p>

      <p>
        Related: <Link to="/docs/security/popia">POPIA Compliance</Link>{' '}
        covers the broader legal framework, and{' '}
        <Link to="/docs/security/data-export">Data Export</Link> describes
        how to retrieve your data at any time.
      </p>
    </>
  );
};

export default DataResidency;
